// Éditeur des additional_databases pour un déploiement PostgreSQL managé.
//
// - Liste les DBs actuelles (masked côté GET : has_password bool, jamais le password en clair)
// - Permet d'ajouter/retirer des lignes (name + user + password)
// - Bouton "Enregistrer" : PUT /api/v1/deployments/deployment/{id} avec additional_databases updated
// - Bouton "Synchroniser sur le cluster" : POST /sync-databases (crée role+DB en superuser
//   sur les clusters CNPG déjà bootstrapés — CNPG ne rejoue jamais postInitApplicationSQL)
//
// Un password laissé vide sur une ligne existante = préserver l'ancien
// (pas de re-chiffrement, le backend voit un dict sans "password").

import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { showErrorToast } from '../utils/toastUtils';
import { isValidPgIdentifier, isValidPgPassword, suggestPgIdentifier } from '../utils/pgIdentifier';
import { useTranslation } from 'react-i18next';

interface AdditionalDbRow {
  name: string;
  user: string;
  password: string;         // vide = préserver l'ancien pour une ligne existante
  has_password?: boolean;   // true = mot de passe stocké côté backend (pour l'affichage placeholder)
  existing?: boolean;       // true = venait du GET, false = ajouté dans le UI
}

interface AdditionalDatabasesManagerProps {
  deploymentId: string;
  initial: Array<{name: string; user?: string; owner?: string; has_password: boolean}>;
  onSaved?: () => void;
}

const AdditionalDatabasesManager: React.FC<AdditionalDatabasesManagerProps> = ({
  deploymentId,
  initial,
  onSaved,
}) => {
  const { t } = (useTranslation as any)('deployments');
  const [rows, setRows] = useState<AdditionalDbRow[]>(
    (initial || []).map((d) => ({
      name: d.name,
      user: d.user || d.owner || '',
      password: '',
      has_password: d.has_password,
      existing: true,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const addRow = () =>
    setRows([...rows, { name: '', user: '', password: '', existing: false }]);
  const removeRow = (idx: number) => setRows(rows.filter((_, i) => i !== idx));
  const patchRow = (idx: number, patch: Partial<AdditionalDbRow>) =>
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  // Validation client-side alignee sur le validator backend. Sans elle,
  // l'utilisateur decouvrait le probleme via un 422 apres aller-retour reseau
  // (et, avant le fix de getApiErrorMessage, sans meme voir le motif).
  // On ne valide QUE ce que l'utilisateur peut corriger : sur une ligne existante,
  // name/user sont disabled. Une base legacy au nom invalide (creee avant
  // l'ajout du validator backend) bloquerait sinon le bouton Enregistrer pour
  // toujours — impossible meme de changer un mot de passe.
  const rowErrors = useMemo(
    () =>
      rows.map((row) => ({
        name:
          !row.existing && row.name.trim().length > 0 && !isValidPgIdentifier(row.name.trim())
            ? (t('deploy.additionalDatabasesInvalidName') as string)
            : null,
        user:
          !row.existing && row.user.trim().length > 0 && !isValidPgIdentifier(row.user.trim())
            ? (t('deploy.additionalDatabasesInvalidName') as string)
            : null,
        // Password vide = "preserver l'existant" cote backend, donc pas valide ici.
        password:
          row.password.length > 0 && !isValidPgPassword(row.password)
            ? (t('deploy.additionalDatabasesInvalidPassword') as string)
            : null,
      })),
    [rows, t]
  );

  const hasErrors = rowErrors.some((e) => e.name || e.user || e.password);

  const handleSave = async () => {
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = rows
        .filter((r) => r.name.trim().length > 0)
        .map((r) => ({
          name: r.name.trim(),
          ...(r.user.trim() ? { user: r.user.trim() } : {}),
          // Password vide sur une ligne existante = préserver (pas envoyé au backend).
          // Password vide sur une NOUVELLE ligne = pas de user dédié (fallback owner).
          ...(r.password ? { password: r.password } : {}),
        }));
      await apiService.updateDeployment(deploymentId, { additional_databases: payload });
      toast.success(t('deploy.additionalDatabasesSaved') as string);
      onSaved?.();
    } catch (err: unknown) {
      // showErrorToast extrait le detail d'un 422 FastAPI (nom du champ + motif)
      // et logge l'erreur complete. `err.message` ne donnait que
      // "Request failed with status code 422".
      showErrorToast(err, t('deploy.additionalDatabasesSaveFailed') as string, 'Save additional databases');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await apiService.syncAdditionalDatabases(deploymentId);
      if (res.status === 'Succeeded') {
        toast.success(t('deploy.additionalDatabasesSyncSuccess') as string);
      } else {
        toast.error(`${t('deploy.additionalDatabasesSyncFailed') as string} : ${res.logs?.substring(0, 200) || 'unknown'}`);
      }
    } catch (err: unknown) {
      showErrorToast(err, t('deploy.additionalDatabasesSyncFailed') as string, 'Sync additional databases');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mt-6 p-4 bg-surface-2 rounded-lg border border-border-primary">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-text-primary">
            {t('deploy.additionalDatabases') as string}
          </h3>
          <p className="text-xs text-text-tertiary mt-0.5">
            {t('deploy.additionalDatabasesHelp') as string}
          </p>
          <p className="text-xs text-text-tertiary mt-0.5">
            {t('deploy.additionalDatabasesNameHint') as string}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div key={idx} className="flex items-start gap-2 p-3 bg-surface-3 rounded-md border border-border-primary">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 flex-1">
              <div>
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => patchRow(idx, { name: e.target.value })}
                  placeholder={t('deploy.additionalDatabasesNamePlaceholder') as string}
                  disabled={row.existing}
                  aria-invalid={!!rowErrors[idx]?.name}
                  className={`w-full px-3 py-2 border bg-surface-1 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 disabled:opacity-60 ${rowErrors[idx]?.name ? 'border-red-500' : 'border-border-primary'}`}
                />
                {rowErrors[idx]?.name && (
                  <p className="mt-1 text-xs text-red-400">
                    {rowErrors[idx].name}
                    {suggestPgIdentifier(row.name.trim()) && (
                      <button
                        type="button"
                        onClick={() => patchRow(idx, { name: suggestPgIdentifier(row.name.trim()) as string })}
                        className="ml-1 underline hover:text-red-300"
                      >
                        → {suggestPgIdentifier(row.name.trim())}
                      </button>
                    )}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={row.user}
                  onChange={(e) => patchRow(idx, { user: e.target.value })}
                  placeholder={t('deploy.additionalDatabasesUserPlaceholder') as string}
                  disabled={row.existing}
                  aria-invalid={!!rowErrors[idx]?.user}
                  className={`w-full px-3 py-2 border bg-surface-1 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 disabled:opacity-60 ${rowErrors[idx]?.user ? 'border-red-500' : 'border-border-primary'}`}
                />
                {rowErrors[idx]?.user && (
                  <p className="mt-1 text-xs text-red-400">
                    {rowErrors[idx].user}
                    {suggestPgIdentifier(row.user.trim()) && (
                      <button
                        type="button"
                        onClick={() => patchRow(idx, { user: suggestPgIdentifier(row.user.trim()) as string })}
                        className="ml-1 underline hover:text-red-300"
                      >
                        → {suggestPgIdentifier(row.user.trim())}
                      </button>
                    )}
                  </p>
                )}
              </div>
              <div>
                <input
                  type="password"
                  value={row.password}
                  onChange={(e) => patchRow(idx, { password: e.target.value })}
                  placeholder={row.existing && row.has_password ? '●●●●●●●● (inchangé si vide)' : (t('deploy.additionalDatabasesPasswordPlaceholder') as string)}
                  aria-invalid={!!rowErrors[idx]?.password}
                  className={`w-full px-3 py-2 border bg-surface-1 text-text-primary rounded-md text-sm focus:outline-none focus:ring-accent-500 ${rowErrors[idx]?.password ? 'border-red-500' : 'border-border-primary'}`}
                />
                {rowErrors[idx]?.password && (
                  <p className="mt-1 text-xs text-red-400">{rowErrors[idx].password}</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="px-3 py-2 text-red-400 hover:text-red-300 text-sm"
              title={t('deploy.additionalDatabasesRemove') as string}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          className="w-full px-3 py-2 border border-dashed border-border-primary text-text-secondary rounded-md text-sm hover:bg-surface-3"
        >
          + {t('deploy.additionalDatabasesAdd') as string}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || hasErrors}
          className="px-4 py-2 bg-accent-600 text-white text-sm font-medium rounded-md hover:bg-accent-700 disabled:opacity-50"
        >
          {saving
            ? (t('deploy.additionalDatabasesSaving') as string)
            : (t('deploy.additionalDatabasesSave') as string)}
        </button>
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing}
          className="px-4 py-2 bg-surface-3 text-text-primary border border-border-primary text-sm font-medium rounded-md hover:bg-surface-4 disabled:opacity-50"
          title="POST /sync-databases : crée role+DB en superuser sur le cluster existant"
        >
          {syncing
            ? (t('deploy.additionalDatabasesSyncing') as string)
            : (t('deploy.additionalDatabasesSync') as string)}
        </button>
      </div>
    </div>
  );
};

export default AdditionalDatabasesManager;
